import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCw, Trash2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtPrice } from "@/lib/api";

const label = (q) => [[q.year, q.make, q.model].filter(Boolean).join(" "), q.part, q.part_number].filter(Boolean).join(" — ");

const lowest = (s) => {
  const p = (s.listings || []).map((l) => l.price).filter((x) => x != null);
  return p.length ? Math.min(...p) : null;
};

export default function HistoryPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/searches").then(({ data }) => setItems(data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/searches/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Search removed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-5xl">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-3">// Job log</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase mb-10">Search history</h1>
        {items === null && <p className="text-muted-foreground" data-testid="history-loading">Loading…</p>}
        {items?.length === 0 && (
          <div className="border border-dashed border-border rounded-sm p-10 text-center" data-testid="history-empty">
            <p className="text-muted-foreground mb-4">No searches yet.</p>
            <Button asChild className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="history-start-btn"><Link to="/app/parts">Run your first search</Link></Button>
          </div>
        )}
        <ul className="divide-y divide-border border border-border rounded-sm bg-card/80" data-testid="history-list">
          {items?.map((s, i) => (
            <li key={s.id} className="rise flex flex-col sm:flex-row sm:items-center gap-4 p-5 hover:bg-secondary/40 transition-colors" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`history-item-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" data-testid={`history-label-${s.id}`}>{label(s.query)}</p>
                <p className="font-mono text-xs text-muted-foreground mt-1">
                  {new Date(s.created_at).toLocaleString()} · {s.listings?.length || 0} listings · from {fmtPrice(lowest(s))}
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-sm hover:text-white" data-testid={`history-open-${s.id}`}>
                  <Link to={`/app/parts?id=${s.id}`}><ArrowUpRight className="h-4 w-4 mr-1" />Open</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="rounded-sm hover:text-white" data-testid={`history-rerun-${s.id}`}>
                  <Link to={`/app/parts?id=${s.id}&rerun=1`}><RotateCw className="h-4 w-4 mr-1" />Re-run</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(s.id)} className="text-muted-foreground hover:text-red-400" data-testid={`history-delete-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
    </div>
  );
}
